import { supabase } from "../supabaseClient";
import {
  PayrollCountry,
  PayrollTemplate,
  PayrollTemplateRole,
  PayrollTemplateSummary,
  PayrollTemplateWithRoles,
  CountrySalaryBaseline,
  CreateTemplateInput,
  UpdateTemplateInput,
  CreateTemplateRoleInput,
  UpdateTemplateRoleInput,
  CreateBaselineInput,
  TemplateSearchResult,
  PropertyType,
} from "../../types/payrollTemplate";

export async function getAllCountries(): Promise<PayrollCountry[]> {
  const { data, error } = await supabase
    .from("payroll_countries")
    .select("*")
    .eq("is_active", true)
    .order("name");

  if (error) {
    console.error("Error fetching countries:", error);
    throw new Error(`Failed to fetch countries: ${error.message}`);
  }

  return (data || []).map((row) => ({
    code: row.code,
    name: row.name,
    region: row.region,
    currencyCode: row.currency_code,
    isActive: row.is_active,
    createdAt: row.created_at,
  }));
}

export async function getCountryByCode(
  code: string,
): Promise<PayrollCountry | null> {
  const { data, error } = await supabase
    .from("payroll_countries")
    .select("*")
    .eq("code", code)
    .single();

  if (error || !data) {
    return null;
  }

  return {
    code: data.code,
    name: data.name,
    region: data.region,
    currencyCode: data.currency_code,
    isActive: data.is_active,
    createdAt: data.created_at,
  };
}

export async function isValidCountryCode(code: string): Promise<boolean> {
  const { data, error } = await supabase
    .from("payroll_countries")
    .select("code")
    .eq("code", code)
    .eq("is_active", true)
    .single();

  return !error && !!data;
}

export async function getCountryOrFallback(
  code: string,
): Promise<PayrollCountry> {
  const country = await getCountryByCode(code);

  if (country) {
    return country;
  }

  // Fallback to global
  const global = await getCountryByCode("XX");
  if (global) {
    console.warn(`Country ${code} not found, using global fallback`);
    return global;
  }

  // Should never happen with seed data, but just in case
  throw new Error(`Country ${code} not found and no global fallback available`);
}

export async function getCountryBaseline(
  countryCode: string,
  year: number = new Date().getFullYear(),
): Promise<CountrySalaryBaseline | null> {
  const { data, error } = await supabase
    .from("country_salary_baselines")
    .select("*")
    .eq("country_code", countryCode)
    .eq("year", year)
    .single();

  if (error || !data) {
    // Try previous year as fallback
    const { data: prevYearData } = await supabase
      .from("country_salary_baselines")
      .select("*")
      .eq("country_code", countryCode)
      .lte("year", year)
      .order("year", { ascending: false })
      .limit(1)
      .single();

    if (!prevYearData) {
      return null;
    }

    return mapBaselineFromDb(prevYearData);
  }

  return mapBaselineFromDb(data);
}

export async function getBaselineOrFallback(
  countryCode: string,
  year: number = new Date().getFullYear(),
): Promise<CountrySalaryBaseline> {
  const baseline = await getCountryBaseline(countryCode, year);

  if (baseline) {
    return baseline;
  }

  // Try global fallback
  const globalBaseline = await getCountryBaseline("XX", year);
  if (globalBaseline) {
    console.warn(
      `Baseline for ${countryCode} not found, using global fallback`,
    );
    return globalBaseline;
  }

  // Critical failure - should not happen with seed data
  throw new Error(`No baseline found for ${countryCode} or global fallback`);
}

/**
 * Get all baselines (for admin UI)
 */
export async function getAllBaselines(): Promise<CountrySalaryBaseline[]> {
  const { data, error } = await supabase
    .from("country_salary_baselines")
    .select("*")
    .order("country_code")
    .order("year", { ascending: false });

  if (error) {
    console.error("Error fetching baselines:", error);
    throw new Error(`Failed to fetch baselines: ${error.message}`);
  }

  return (data || []).map(mapBaselineFromDb);
}

/**
 * Create or update a country baseline
 */
export async function upsertCountryBaseline(
  input: CreateBaselineInput,
): Promise<CountrySalaryBaseline> {
  const { data, error } = await supabase
    .from("country_salary_baselines")
    .upsert(
      {
        country_code: input.countryCode,
        year: input.year,
        receptionist_annual_gross: input.receptionistAnnualGross,
        employer_cost_pct_min: input.employerCostPctMin ?? 20,
        employer_cost_pct_typical: input.employerCostPctTypical ?? 25,
        employer_cost_pct_max: input.employerCostPctMax ?? 30,
        currency_code: input.currencyCode ?? "EUR",
        notes: input.notes,
        updated_at: new Date().toISOString(),
      },
      {
        onConflict: "country_code,year",
      },
    )
    .select()
    .single();

  if (error) {
    console.error("Error upserting baseline:", error);
    throw new Error(`Failed to save baseline: ${error.message}`);
  }

  return mapBaselineFromDb(data);
}

export async function getAllTemplates(): Promise<PayrollTemplate[]> {
  const { data, error } = await supabase
    .from("payroll_templates")
    .select("*")
    .eq("is_active", true)
    .order("country_code")
    .order("name");

  if (error) {
    console.error("Error fetching templates:", error);
    throw new Error(`Failed to fetch templates: ${error.message}`);
  }

  return (data || []).map(mapTemplateFromDb);
}

/**
 * Get template summaries (with role counts)
 */
export async function getTemplateSummaries(): Promise<
  PayrollTemplateSummary[]
> {
  const { data, error } = await supabase
    .from("payroll_template_summary")
    .select("*")
    .order("country_code")
    .order("name");

  if (error) {
    console.error("Error fetching template summaries:", error);
    throw new Error(`Failed to fetch template summaries: ${error.message}`);
  }

  return (data || []).map((row) => ({
    id: row.id,
    name: row.name,
    countryCode: row.country_code,
    countryName: row.country_name,
    propertyType: row.property_type,
    minRooms: row.min_rooms,
    maxRooms: row.max_rooms,
    isDefault: row.is_default,
    isActive: row.is_active,
    roleCount: row.role_count,
    createdBy: null,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  }));
}

/**
 * Get a template by ID
 */
export async function getTemplateById(
  id: string,
): Promise<PayrollTemplate | null> {
  const { data, error } = await supabase
    .from("payroll_templates")
    .select("*")
    .eq("id", id)
    .single();

  if (error || !data) {
    return null;
  }

  return mapTemplateFromDb(data);
}

/**
 * Get templates by country
 */
export async function getTemplatesByCountry(
  countryCode: string,
): Promise<PayrollTemplate[]> {
  const { data, error } = await supabase
    .from("payroll_templates")
    .select("*")
    .eq("country_code", countryCode)
    .eq("is_active", true)
    .order("is_default", { ascending: false })
    .order("name");

  if (error) {
    console.error("Error fetching templates by country:", error);
    return [];
  }

  return (data || []).map(mapTemplateFromDb);
}

export async function findBestTemplate(
  countryCode: string,
  rooms: number,
  propertyType?: PropertyType,
): Promise<TemplateSearchResult | null> {
  // First validate country exists or use fallback
  const country = await getCountryOrFallback(countryCode);
  const searchCountry = country.code;

  // Call database function for smart matching
  const { data, error } = await supabase.rpc("find_best_payroll_template", {
    p_country_code: searchCountry,
    p_rooms: rooms,
    p_property_type: propertyType || null,
  });

  if (error || !data) {
    console.warn(
      `No template found for ${searchCountry}, ${rooms} rooms, ${propertyType}`,
    );
    return null;
  }

  // Get the full template
  const template = await getTemplateById(data);
  if (!template) {
    return null;
  }

  // Determine match type and score
  let matchType: TemplateSearchResult["matchType"] = "global_fallback";
  let matchScore = 0;

  if (template.countryCode === countryCode) {
    if (template.propertyType === propertyType) {
      matchType = "exact";
      matchScore = 100;
    } else if (template.minRooms && template.maxRooms) {
      matchType = "country_rooms";
      matchScore = 80;
    } else if (template.isDefault) {
      matchType = "country_default";
      matchScore = 60;
    }
  } else if (template.countryCode === "XX") {
    matchType = "global_fallback";
    matchScore = 40;
  }

  return {
    template,
    matchType,
    matchScore,
  };
}

/**
 * Create a new template
 */
export async function createTemplate(
  input: CreateTemplateInput,
): Promise<PayrollTemplate> {
  // Validate country exists
  const isValid = await isValidCountryCode(input.countryCode);
  if (!isValid) {
    throw new Error(`Invalid country code: ${input.countryCode}`);
  }

  const { data, error } = await supabase
    .from("payroll_templates")
    .insert({
      name: input.name,
      country_code: input.countryCode,
      property_type: input.propertyType,
      min_rooms: input.minRooms,
      max_rooms: input.maxRooms,
      description: input.description,
      is_default: input.isDefault ?? false,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    console.error("Error creating template:", error);
    throw new Error(`Failed to create template: ${error.message}`);
  }

  return mapTemplateFromDb(data);
}

/**
 * Update a template
 */
export async function updateTemplate(
  id: string,
  updates: UpdateTemplateInput,
): Promise<PayrollTemplate> {
  const updateData: any = {
    updated_at: new Date().toISOString(),
  };

  if (updates.name !== undefined) updateData.name = updates.name;
  if (updates.countryCode !== undefined) {
    const isValid = await isValidCountryCode(updates.countryCode);
    if (!isValid) {
      throw new Error(`Invalid country code: ${updates.countryCode}`);
    }
    updateData.country_code = updates.countryCode;
  }
  if (updates.propertyType !== undefined)
    updateData.property_type = updates.propertyType;
  if (updates.minRooms !== undefined) updateData.min_rooms = updates.minRooms;
  if (updates.maxRooms !== undefined) updateData.max_rooms = updates.maxRooms;
  if (updates.description !== undefined)
    updateData.description = updates.description;
  if (updates.isDefault !== undefined)
    updateData.is_default = updates.isDefault;
  if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

  const { data, error } = await supabase
    .from("payroll_templates")
    .update(updateData)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating template:", error);
    throw new Error(`Failed to update template: ${error.message}`);
  }

  return mapTemplateFromDb(data);
}

/**
 * Delete a template (soft delete)
 */
export async function deleteTemplate(id: string): Promise<void> {
  const { error } = await supabase
    .from("payroll_templates")
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq("id", id);

  if (error) {
    console.error("Error deleting template:", error);
    throw new Error(`Failed to delete template: ${error.message}`);
  }
}

/**
 * Duplicate a template
 */
export async function duplicateTemplate(
  id: string,
  newName: string,
): Promise<PayrollTemplate> {
  // Get original template
  const original = await getTemplateById(id);
  if (!original) {
    throw new Error("Template not found");
  }

  // Get all roles
  const roles = await getTemplateRoles(id);

  // Create new template
  const newTemplate = await createTemplate({
    name: newName,
    countryCode: original.countryCode,
    propertyType: original.propertyType,
    minRooms: original.minRooms,
    maxRooms: original.maxRooms,
    description: original.description,
    isDefault: false, // Don't duplicate the default flag
  });

  // Duplicate all roles
  for (const role of roles) {
    await addTemplateRole({
      templateId: newTemplate.id,
      department: role.department,
      title: role.title,
      ftePerRoom: role.ftePerRoom,
      minFtes: role.minFtes,
      maxFtes: role.maxFtes,
      salaryMultiplier: role.salaryMultiplier,
      employerCostPct: role.employerCostPct,
      sortOrder: role.sortOrder,
      notes: role.notes,
    });
  }

  return newTemplate;
}

export async function getTemplateRoles(
  templateId: string,
): Promise<PayrollTemplateRole[]> {
  const { data, error } = await supabase
    .from("payroll_template_roles")
    .select("*")
    .eq("template_id", templateId)
    .eq("is_active", true)
    .order("department")
    .order("sort_order");

  if (error) {
    console.error("Error fetching template roles:", error);
    throw new Error(`Failed to fetch template roles: ${error.message}`);
  }

  return (data || []).map(mapRoleFromDb);
}

/**
 * Get template with all its roles and baseline
 */
export async function getTemplateWithRoles(
  templateId: string,
): Promise<PayrollTemplateWithRoles | null> {
  const template = await getTemplateById(templateId);
  if (!template) {
    return null;
  }

  const roles = await getTemplateRoles(templateId);
  const baseline = await getCountryBaseline(template.countryCode);

  return {
    ...template,
    roles,
    baseline: baseline || undefined,
  };
}

/**
 * Add a role to a template
 */
export async function addTemplateRole(
  input: CreateTemplateRoleInput,
): Promise<PayrollTemplateRole> {
  const { data, error } = await supabase
    .from("payroll_template_roles")
    .insert({
      template_id: input.templateId,
      department: input.department,
      title: input.title,
      fte_per_room: input.ftePerRoom,
      min_ftes: input.minFtes ?? 0,
      max_ftes: input.maxFtes,
      salary_multiplier: input.salaryMultiplier,
      employer_cost_pct: input.employerCostPct,
      sort_order: input.sortOrder ?? 0,
      notes: input.notes,
      is_active: true,
    })
    .select()
    .single();

  if (error) {
    console.error("Error adding template role:", error);
    throw new Error(`Failed to add template role: ${error.message}`);
  }

  return mapRoleFromDb(data);
}

/**
 * Update a template role
 */
export async function updateTemplateRole(
  id: string,
  updates: UpdateTemplateRoleInput,
): Promise<PayrollTemplateRole> {
  const updateData: any = {
    updated_at: new Date().toISOString(),
  };

  if (updates.department !== undefined)
    updateData.department = updates.department;
  if (updates.title !== undefined) updateData.title = updates.title;
  if (updates.ftePerRoom !== undefined)
    updateData.fte_per_room = updates.ftePerRoom;
  if (updates.minFtes !== undefined) updateData.min_ftes = updates.minFtes;
  if (updates.maxFtes !== undefined) updateData.max_ftes = updates.maxFtes;
  if (updates.salaryMultiplier !== undefined)
    updateData.salary_multiplier = updates.salaryMultiplier;
  if (updates.employerCostPct !== undefined)
    updateData.employer_cost_pct = updates.employerCostPct;
  if (updates.sortOrder !== undefined)
    updateData.sort_order = updates.sortOrder;
  if (updates.notes !== undefined) updateData.notes = updates.notes;
  if (updates.isActive !== undefined) updateData.is_active = updates.isActive;

  const { data, error } = await supabase
    .from("payroll_template_roles")
    .update(updateData)
    .eq("id", id)
    .select()
    .single();

  if (error) {
    console.error("Error updating template role:", error);
    throw new Error(`Failed to update template role: ${error.message}`);
  }

  return mapRoleFromDb(data);
}

/**
 * Delete a template role (soft delete)
 */
export async function deleteTemplateRole(id: string): Promise<void> {
  const { error } = await supabase
    .from("payroll_template_roles")
    .update({ is_active: false, updated_at: new Date().toISOString() })
    .eq("id", id);

  if (error) {
    console.error("Error deleting template role:", error);
    throw new Error(`Failed to delete template role: ${error.message}`);
  }
}

/**
 * Reorder roles within a department
 */
export async function reorderTemplateRoles(
  roleIds: string[],
  startOrder: number = 0,
): Promise<void> {
  const updates = roleIds.map((id, index) =>
    supabase
      .from("payroll_template_roles")
      .update({ sort_order: startOrder + index })
      .eq("id", id),
  );

  const results = await Promise.all(updates);
  const errors = results.filter((r) => r.error);

  if (errors.length > 0) {
    console.error("Error reordering roles:", errors);
    throw new Error("Failed to reorder some roles");
  }
}

function mapTemplateFromDb(row: any): PayrollTemplate {
  return {
    id: row.id,
    name: row.name,
    countryCode: row.country_code,
    propertyType: row.property_type,
    minRooms: row.min_rooms,
    maxRooms: row.max_rooms,
    description: row.description,
    isDefault: row.is_default,
    isActive: row.is_active,
    createdBy: row.created_by,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapRoleFromDb(row: any): PayrollTemplateRole {
  return {
    id: row.id,
    templateId: row.template_id,
    department: row.department,
    title: row.title,
    ftePerRoom: Number(row.fte_per_room),
    minFtes: Number(row.min_ftes),
    maxFtes: row.max_ftes ? Number(row.max_ftes) : undefined,
    salaryMultiplier: Number(row.salary_multiplier),
    employerCostPct: Number(row.employer_cost_pct),
    sortOrder: row.sort_order,
    isActive: row.is_active,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}

function mapBaselineFromDb(row: any): CountrySalaryBaseline {
  return {
    id: row.id,
    countryCode: row.country_code,
    year: row.year,
    receptionistAnnualGross: row.receptionist_annual_gross,
    employerCostPctMin: Number(row.employer_cost_pct_min),
    employerCostPctTypical: Number(row.employer_cost_pct_typical),
    employerCostPctMax: Number(row.employer_cost_pct_max),
    currencyCode: row.currency_code,
    notes: row.notes,
    createdAt: row.created_at,
    updatedAt: row.updated_at,
  };
}
